/* eslint-disable react/no-unknown-property */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable max-len */
import React from 'react';
import { useFormik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import * as yup from 'yup';
import classNames from 'classnames';

import Button from 'components/UI_kit_3/Button/Button';
import RouterLinkButton from 'components/UI_kit_3/Button/RouterLinkButton';
import FormText from 'components/UI_kit_3/FormText/FormText';
import FormTextMasked from 'components/UI_kit_3/FormText/FormTextMasked';
import PhoneInput from 'components/UI_kit_3/FormText/PhoneInput/index';
import SwitchButton, { ISwitchValues } from 'components/UI_kit_3/SwitchButton/SwitchButton';
import { getUtm } from 'components/SelfRegistration/utm';
import { getPhoneFormat } from 'components/SelfRegistration/phone-format';
import { COUNTDOWN_KEYS, readStep, writeStep } from 'components/SelfRegistration/persisted-step';
import {
  CODE_LOCK_MINUTES,
  EMAIL_RESEND_COOLDOWN_SECONDS,
  ISelfRegistrationForm,
  RESEND_COOLDOWN_SECONDS,
  SELF_REG_ERROR_CODES,
} from 'components/SelfRegistration/types/dtos';
import { CompanyType, PhoneCodeChannel } from 'components/SelfRegistration/types/enums';
import useAsyncFetch, { AsyncFetchError } from 'hooks/async-fetch';
import { formatCountdown } from 'hooks/countdown';
import usePersistedCountdown from 'hooks/persisted-countdown';
import { authService } from 'services/api/auth';
import { ErrorObj } from 'services/api/axios';
import { selfRegistrationService } from 'services/api/self-registration';

import styles from './RegistrationForm.module.scss';

const toApiPhone = (phone: string) => `+${phone}`;

const findError = (errors: ErrorObj[] | undefined, code: number) => errors?.find((err) => err.code === code);

const phoneFormat = getPhoneFormat();

const RegistrationForm: React.FC = () => {
  const [t, i18n] = useTranslation();
  // const captchaRef = React.useRef<ICaptchaRef>(null);

  const [savedStep] = React.useState(readStep);

  const [codeSent, setCodeSent] = React.useState(savedStep.codeSent);
  const [registeredEmail, setRegisteredEmail] = React.useState<string | null>(savedStep.registeredEmail);
  const [emailTaken, setEmailTaken] = React.useState(false);
  const [commonError, setCommonError] = React.useState<string | null>(null);
  const [codeChannel, setCodeChannel] = React.useState(PhoneCodeChannel.Sms);

  const resendCode = usePersistedCountdown(COUNTDOWN_KEYS.resendCode);
  const codeLock = usePersistedCountdown(COUNTDOWN_KEYS.codeLock);
  const resendEmail = usePersistedCountdown(COUNTDOWN_KEYS.resendEmail);

  React.useEffect(() => {
    let cancelled = false;
    selfRegistrationService.getPhoneCodeChannel()
      .then(({ data }) => {
        if (!cancelled && data.data?.channel) setCodeChannel(data.data.channel);
      })
      // при ошибке остаётся SMS по умолчанию
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const companyTypes = React.useMemo<ISwitchValues<CompanyType>>(() => [
    { value: CompanyType.Individual, title: t('self_registration.individual') },
    { value: CompanyType.Juridical, title: t('self_registration.juridical') },
  ], [t]);

  const schema = React.useMemo(() => yup.object().shape({
    name: yup.string().trim().required(t('form.error.required')).max(255, t('form.error.wrong_value')),
    contactPerson: yup.string().trim().when('companyType', {
      is: CompanyType.Juridical,
      then: (field) => field.required(t('form.error.required')).max(255, t('form.error.wrong_value')),
      otherwise: (field) => field.notRequired(),
    }),
    email: yup.string().trim().lowercase()
      .email(t('form.error.wrong_value'))
      .required(t('form.error.required')),
    phone: yup.string().required(t('form.error.required')).matches(phoneFormat.pattern, t('form.error.wrong_value')),
    // captchaToken: isCaptchaEnabled
    //   ? yup.string().required(t('self_registration.error.captcha_required'))
    //   : yup.string().notRequired(),
    code: codeSent
      ? yup.string().required(t('form.error.required')).matches(/^\d{6}$/, t('form.error.wrong_value'))
      : yup.string().notRequired(),
  }), [t, codeSent]);

  const [sendPhoneCode, sendCodeLoading] = useAsyncFetch({
    async asyncFunc(values: ISelfRegistrationForm) {
      setCommonError(null);
      const { data } = await selfRegistrationService.sendPhoneCode({
        phone: toApiPhone(values.phone),
        email: values.email,
        utm: getUtm(),
        // ...(isCaptchaEnabled ? { captchaToken: values.captchaToken } : {}),
      }, i18n.language);
      setCodeSent(true);
      codeLock.stop();
      resendCode.start(data.data?.resendAfterSeconds || RESEND_COOLDOWN_SECONDS);
    },
    errorHandler: ({ error, errMessage }: AsyncFetchError<[ISelfRegistrationForm]>) => {
      const errors = error.response?.data?.errors as ErrorObj[] | undefined;

      // if (findError(errors, SELF_REG_ERROR_CODES.CAPTCHA_FAILED)) {
      //   // токен одноразовый, виджет надо перерисовать и взять новый
      //   captchaRef.current?.reset();
      //   form.setFieldError('captchaToken', errMessage);
      //   return;
      // }

      if (findError(errors, SELF_REG_ERROR_CODES.CODE_ALREADY_SENT)) {
        setCodeSent(true);
        resendCode.start(RESEND_COOLDOWN_SECONDS);
        return;
      }

      if (findError(errors, SELF_REG_ERROR_CODES.PHONE_TAKEN)) {
        form.setFieldError('phone', t('self_registration.error.phone_taken'));
        return;
      }

      if (findError(errors, SELF_REG_ERROR_CODES.EMAIL_TAKEN)) {
        form.setFieldError('email', t('self_registration.error.email_taken'));
        return;
      }

      form.setFieldError('phone', errMessage);
    },
  });

  const register = React.useCallback(async (values: ISelfRegistrationForm) => {
    const isJuridical = values.companyType === CompanyType.Juridical;
    const { data } = await selfRegistrationService.register({
      companyType: values.companyType,
      name: values.name.trim(),
      contactPerson: isJuridical ? values.contactPerson.trim() : undefined,
      email: values.email.trim().toLowerCase(),
      phone: toApiPhone(values.phone),
    }, i18n.language);
    setRegisteredEmail(data.data?.email || values.email.trim().toLowerCase());
    resendEmail.start(EMAIL_RESEND_COOLDOWN_SECONDS);
  }, [i18n.language, resendEmail]);

  const [confirmPhoneCode, confirmLoading] = useAsyncFetch({
    async asyncFunc(values: ISelfRegistrationForm) {
      setCommonError(null);
      setEmailTaken(false);
      await selfRegistrationService.confirmPhoneCode({
        phone: toApiPhone(values.phone),
        // маска отдаёт unmask-значение, в code уже только 6 цифр
        code: values.code,
      }, i18n.language);
      await register(values);
    },
    errorHandler: ({ error, errMessage }: AsyncFetchError<[ISelfRegistrationForm]>) => {
      const errors = error.response?.data?.errors as ErrorObj[] | undefined;

      if (findError(errors, SELF_REG_ERROR_CODES.TOO_MANY_ATTEMPTS)) {
        // блокировка ввода кода на 15 минут, форма остаётся заполненной
        codeLock.start(CODE_LOCK_MINUTES * 60);
        resendCode.stop();
        form.setFieldError('code', t('self_registration.error.too_many_attempts', { minutes: CODE_LOCK_MINUTES }));
        return;
      }

      if (findError(errors, SELF_REG_ERROR_CODES.WRONG_CODE)) {
        // в сообщении бэкенда уже указано количество оставшихся попыток
        form.setFieldError('code', errMessage);
        return;
      }

      if (findError(errors, SELF_REG_ERROR_CODES.EMAIL_TAKEN)) {
        setEmailTaken(true);
        form.setFieldError('email', t('self_registration.error.email_taken'));
        return;
      }

      if (findError(errors, SELF_REG_ERROR_CODES.PHONE_TAKEN)) {
        form.setFieldError('phone', t('self_registration.error.phone_taken'));
        return;
      }

      if (findError(errors, SELF_REG_ERROR_CODES.PHONE_NOT_CONFIRMED)) {
        setCodeSent(false);
        resendCode.stop();
        setCommonError(errMessage);
        return;
      }

      form.setFieldError('code', errMessage);
    },
  });

  /** Повторная отправка письма со ссылкой для установки пароля */
  const [sendEmailAgain, sendEmailLoading] = useAsyncFetch({
    async asyncFunc() {
      await authService.resetPasswordSendEmail(registeredEmail || '', i18n.language);
      resendEmail.start(EMAIL_RESEND_COOLDOWN_SECONDS);
    },
    errorNote: true,
  });

  const form = useFormik<ISelfRegistrationForm>({
    initialValues: {
      ...savedStep.values,
      // код из SMS не восстанавливается
      // captchaToken: '',
      code: '',
    },
    validationSchema: schema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: (values) => (codeSent ? confirmPhoneCode(values) : sendPhoneCode(values)),
  });

  const {
    companyType, name, contactPerson, email, phone,
  } = form.values;

  React.useEffect(() => {
    writeStep({
      values: {
        companyType, name, contactPerson, email, phone,
      },
      codeSent,
      registeredEmail,
    });
  }, [companyType, name, contactPerson, email, phone, codeSent, registeredEmail]);

  const isJuridical = form.values.companyType === CompanyType.Juridical;
  const loading = sendCodeLoading || confirmLoading;

  const handlePhoneChange = (value: string) => {
    form.setFieldValue('phone', value);
    // код привязан к номеру, при смене номера шаг подтверждения начинается заново
    if (codeSent) {
      setCodeSent(false);
      setCommonError(null);
      form.setFieldValue('code', '');
      resendCode.stop();
      codeLock.stop();
    }
  };

  if (registeredEmail) {
    return (
      <div className={styles.emailSent} qa-element="registration-email-sent">
        <p>
          <Trans
            i18nKey="self_registration.email_sent_note"
            values={{ email: registeredEmail }}
            components={{ email: <span className={styles.email} /> }}
          />
          <br />
          {t('self_registration.email_sent_hint')}
        </p>
        <div className={styles.resend}>
          {resendEmail.active ? (
            <span qa-element="resend-email-timer">
              {t('self_registration.resend_email_in', { time: formatCountdown(resendEmail.left) })}
            </span>
          ) : (
            <Button
              variant="link"
              disabled={sendEmailLoading}
              onClick={() => sendEmailAgain()}
              qa-element="resend-email"
            >
              {t('self_registration.resend_email')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <form className={styles.form} noValidate onSubmit={form.handleSubmit} id="registration-form">
      <SwitchButton
        className={styles.switch}
        value={form.values.companyType}
        values={companyTypes}
        onChange={(value) => form.setFieldValue('companyType', value)}
        qaElement="registration-company-type"
      />

      <FormText
        className="w-100 mb-2xl"
        id="name"
        name="name"
        type="text"
        required
        label={isJuridical ? t('self_registration.company_name') : t('self_registration.full_name')}
        value={form.values.name}
        onChange={form.handleChange}
        error={form.errors.name}
        isInvalid={!!form.errors.name}
        qaElement="registration-name"
        qaElementFeedback="registration-name-error"
      />

      {isJuridical && (
        <FormText
          className="w-100 mb-2xl"
          id="contactPerson"
          name="contactPerson"
          type="text"
          required
          label={t('self_registration.contact_person')}
          value={form.values.contactPerson}
          onChange={form.handleChange}
          error={form.errors.contactPerson}
          isInvalid={!!form.errors.contactPerson}
          qaElement="registration-contact-person"
          qaElementFeedback="registration-contact-person-error"
        />
      )}

      <FormText
        className="w-100 mb-2xl"
        id="email"
        name="email"
        type="email"
        required
        label={t('common.email')}
        placeholder={t('self_registration.email_placeholder')}
        value={form.values.email}
        onChange={(e) => {
          setEmailTaken(false);
          form.handleChange(e);
        }}
        error={form.errors.email}
        isInvalid={!!form.errors.email}
        qaElement="registration-email"
        qaElementFeedback="registration-email-error"
      />

      {emailTaken && (
        <div className={styles.emailTaken} qa-element="registration-email-taken">
          <RouterLinkButton variant="link" to="/" replace>
            {t('self_registration.error.email_taken_action')}
          </RouterLinkButton>
        </div>
      )}

      {codeSent && (
        <div className={styles.codeSentNote} qa-element="registration-code-note">
          {t('self_registration.code_sent_note')}
        </div>
      )}

      <div className={classNames(styles.phoneRow, 'mb-2xl')}>
        <PhoneInput
          id="phone"
          name="phone"
          label={t('self_registration.phone')}
          placeholder={t(phoneFormat.placeholderKey)}
          mask={phoneFormat.mask}
          inputValue={form.values.phone}
          onChange={handlePhoneChange}
          error={form.errors.phone}
          isInvalid={!!form.errors.phone}
        />
        {codeSent && (
          <FormTextMasked
            id="code"
            name="code"
            type="text"
            mask="000 000"
            unmask
            inputMode="numeric"
            autoComplete="one-time-code"
            label={codeChannel === PhoneCodeChannel.Whatsapp ? t('self_registration.whatsapp_code') : t('self_registration.sms_code')}
            value={form.values.code}
            onAccept={(value) => form.setFieldValue('code', value)}
            disabled={codeLock.active}
            error={form.errors.code}
            isInvalid={!!form.errors.code}
            qa-element="registration-code"
            qaElementFeedback="registration-code-error"
          />
        )}
      </div>

      {codeSent && (
        <div className={classNames(styles.resend, 'mb-2xl')}>
          {resendCode.active || codeLock.active ? (
            <span qa-element="resend-code-timer">
              {codeLock.active
                ? t('self_registration.code_locked_for', { time: formatCountdown(codeLock.left) })
                : t('self_registration.resend_code_in', { time: formatCountdown(resendCode.left) })}
            </span>
          ) : (
            <Button
              variant="link"
              disabled={loading}
              onClick={() => sendPhoneCode(form.values)}
              qa-element="resend-code"
            >
              {t('self_registration.resend_code')}
            </Button>
          )}
        </div>
      )}

      {/* <Captcha
        ref={captchaRef}
        className="mb-2xl"
        label={t('self_registration.captcha')}
        error={form.errors.captchaToken}
        isInvalid={!!form.errors.captchaToken}
        onChange={(token) => {
          form.setFieldValue('captchaToken', token);
          form.setFieldError('captchaToken', '');
        }}
      /> */}

      {commonError && <div className={styles.commonError} qa-element="registration-error">{commonError}</div>}

      <Button
        className="w-100 mb-2xl"
        size="lg"
        type="submit"
        variant="primary"
        loading={loading}
        disabled={loading || codeLock.active}
        qa-element="registration-submit"
      >
        {codeSent ? t('self_registration.confirm') : t('self_registration.continue')}
      </Button>
    </form>
  );
};

export default RegistrationForm;
